/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Presentation Print v2 — Decision Pack
 *
 * Data contract between engine → presentation print.
 * This is the ONLY shape the print layer consumes.
 * Engine truth flows one way; the print layer never recalculates.
 *
 * Global print rules (frozen):
 *  ✅ Print-first (screen just previews)
 *  ✅ Each option = exactly one A4 page
 *  ✅ £/year only
 *  ❌ No replacements
 *  ❌ No timelines
 *  ❌ No carbon
 *  ❌ No "doing nothing"
 *  ❌ No technical tables
 */

import type {
  SystemRecOutput,
  SystemRecInput,
  RecommendationOption,
  OccupancyTiming,
  RetrofitLevel,
  LowTempReadiness,
  SystemDiagramKey,
} from './types';
import { getOccupancyTimingLabel } from './occupancyUtils';
import { formatManifestForPDF, ENGINE_VERSION } from './assumptions';
import { getHeatLossSuitabilityBand } from '../domain/heatLoss/calculateHeatLoss';

// Re-export SystemDiagramKey for rendering layer convenience
export type { SystemDiagramKey } from './types';

// ─── Banned terms ────────────────────────────────────────────────
// If ANY of these appear in printed output, the acceptance test fails.
export const BANNED_TERMS = [
  'replacement',
  'replacements',
  'carbon',
  'CO2',
  'co2',
  'timeline',
  'doing nothing',
  'do nothing',
  'efficiency %',
  'efficiency percentage',
] as const;

// ─── Behavioral Archetypes (de-duplication strategy) ────────────

export type BehavioralArchetype = 'RESPONSIVE_BURST' | 'STEADY_CONTINUOUS' | 'LEGACY_CONSTRAINED';

export interface ArchetypeDefinition {
  archetype: BehavioralArchetype;
  icon: string;
  label: string;
  summary: string;
}

export const BEHAVIORAL_ARCHETYPES: Record<BehavioralArchetype, ArchetypeDefinition> = {
  RESPONSIVE_BURST: {
    archetype: 'RESPONSIVE_BURST',
    icon: '⚡',
    label: 'Responsive & Burst-Ready',
    summary: 'Fast warm-up, flexible routines, handles peak demands easily',
  },
  STEADY_CONTINUOUS: {
    archetype: 'STEADY_CONTINUOUS',
    icon: '🌊',
    label: 'Steady & Continuous',
    summary: 'Slow warm-up, best for predictable operation, very stable once warm',
  },
  LEGACY_CONSTRAINED: {
    archetype: 'LEGACY_CONSTRAINED',
    icon: '🔧',
    label: 'Conventional & Tank-Limited',
    summary: 'Traditional operation, limited by stored water capacity',
  },
};

// ─── Comfort behaviour data ─────────────────────────────────────

export interface ComfortCurvePoint {
  hour: number;
  comfort: number; // 0–100 abstract comfort scale
}

export interface ComfortBehaviour {
  /** Behavioral archetype for this system */
  archetype: BehavioralArchetype;
  /** Chart title */
  chartTitle: string;
  /** Data points for the curve (0–24h) */
  curve: ComfortCurvePoint[];
  /** 2–3 deterministic bullets */
  bullets: string[];
}

// ─── Section data shapes ────────────────────────────────────────

export interface OptionHeader {
  systemName: string;
  subLine: string;
}

export interface WorksBestWhen {
  bullet: string;
}

export interface HotWaterSuitability {
  /** Simplified summary - reference to comparison table */
  summary: string;
  /** Reference text pointing to summary table */
  referenceText: string;
}

export interface SuitabilityForHome {
  /** Hot water suitability - displayed before other factors */
  hotWater: HotWaterSuitability;
  whatHelps: {
    insulationLevel: string;
    emitters: string;
    lowTempReadiness: string;
  };
  thingsToBeAware: string[];
}

export interface AnnualRunningCost {
  currentSystemCostPerYear: number | null;
  thisOptionCostPerYear: number | null;
  footnote: string;
}

// ─── Per-option page data ───────────────────────────────────────

export interface OptionPageData {
  /** Stable option ID from engine */
  optionId: string;
  /** Section A */
  header: OptionHeader;
  /** System diagram key (enum) for print/PDF rendering */
  systemDiagramKey: SystemDiagramKey;
  /** Section B */
  comfort: ComfortBehaviour;
  /** Section C */
  worksBestWhen: WorksBestWhen;
  /** Section D */
  suitability: SuitabilityForHome;
  /** Section E */
  annualCost: AnnualRunningCost;
  /** Section F — planning note (same everywhere) */
  planningNote: string;
  /** Experience fit factors (post-engine presentation layer) */
  experienceFit?: Array<{
    factor: string;
    dots: string;
    verdict?: 'supports' | 'partial' | 'conflicts' | 'unknown';
    comment?: string;
    isConflict?: boolean;
    conflictNote?: string;
  }>;
  /** Control precision data (for gas boiler options) */
  controlPrecision?: {
    showSection: boolean;
    boilerSupportsLoadCompensation: boolean;
  };
  /** Microbore pipework note (if applicable) */
  pipeBoreNote?: string;
  /** Quoted install price (BG style - separate from savings) */
  quotedInstallPriceGBP?: number;
  /** Custom label for quoted price (e.g., "Quoted installed price") */
  quoteLabel?: string;
}

// ─── Cover page data ────────────────────────────────────────────

export interface CoverPageData {
  title: string;
  openingParagraph: string;
  occupancyLine: string;
  /** How you use your home - lifestyle preferences */
  lifestyleSummary?: {
    items: Array<{ label: string; value: string }>;
  };
  assumptionsLine: string | null;
  generatedDate: string;
}

// ─── Summary page data ──────────────────────────────────────────

export interface SummaryRow {
  option: string;
  comfortStyle: string;
  bestFor: string;
  typicalAnnualCost: string;
}

/**
 * Hot water comparison row for the summary table
 */
export interface HotWaterComparisonRow {
  option: string;
  simultaneousOutlets: string;
  recoveryTime: string;
  pressureDependency: string;
  storageVolume: string;
}

export interface SummaryPageData {
  rows: SummaryRow[];
  /** Hot water comparison table - addresses the "See summary table" reference */
  hotWaterComparison: HotWaterComparisonRow[];
  nextSteps: string[];
  /** Confidence summary for print (condensed for 7-page) */
  confidenceSummary?: {
    level: "low" | "medium" | "high";
    reasons: string[];  // max 2 for print
  };
  /** Red flags for print (full detail for 12-page, condensed for 7-page) */
  redFlags?: Array<{
    title: string;
    detail: string;
    severity: "info" | "warning";
  }>;
  /** What could change bullets (max 3 for print) */
  whatCouldChange?: string[];
}

// ─── Appendix data ──────────────────────────────────────────────

export interface AppendixData {
  id: string;
  title: string;
  content: string[];
}

// ─── SWOT Stack data (Portrait No-Brainer) ─────────────────────

export interface SwotNodeData {
  id: string;
  label: string;
  icon?: string;
  impactWeight: number;
}

export interface SwotQuadrantData {
  strengths: SwotNodeData[];
  weaknesses: SwotNodeData[];
  opportunities: SwotNodeData[];
  threats: SwotNodeData[];
}

export interface SwotPageData {
  /** Title for the page */
  title: string;
  /** Status Quo (baseline) SWOT analysis */
  statusQuo: {
    label: string;
    quadrants: SwotQuadrantData;
  };
  /** New Option A SWOT analysis */
  optionA: {
    label: string;
    quadrants: SwotQuadrantData;
  };
}

// ─── Technical Appendix data ────────────────────────────────────

export interface TechnicalDiagramData {
  id: string;
  title: string;
  diagramType: 'laminar-flow' | 's-plan-topology' | 'modulation-cycling';
  description: string;
}

export interface TechnicalAppendixData {
  title: string;
  diagrams: TechnicalDiagramData[];
}

// ─── Technical Disclaimer data ──────────────────────────────────

export interface EngineAssumptionRow {
  parameter: string;
  assumed: string;
  source: string;
}

export interface CalculatedLoadRow {
  metric: string;
  value: string;
  unit: string;
}

export interface TechnicalDisclaimerData {
  title: string;
  engineAssumptions: EngineAssumptionRow[];
  calculatedLoads: CalculatedLoadRow[];
  disclaimers: string[];
}

// ─── Heat Loss Summary Card ─────────────────────────────────────

/**
 * Property heat loss summary card for the printable report.
 * Populated when room-by-room data is available.
 */
export interface HeatLossSummaryCard {
  /** Design temperature pair (e.g., "21°C / -3°C") */
  designTemps: string;
  /** Total heat loss in kW (1 decimal place) */
  totalHeatLossKW: number;
  /** Transmission component in kW */
  transmissionLossKW: number;
  /** Ventilation component in kW */
  ventilationLossKW: number;
  /** Data completeness confidence (0–100) */
  confidence: number;
  /** Recommended sizing target including headroom (kW) */
  sizingTargetKW?: number;
  /** Assumption notes generated by the engine */
  notes?: string[];
  /** Plain-English interpretation of the suitability band */
  interpretation: string;
  /** Flag when confidence is below 60% */
  lowConfidenceFlag: boolean;
  /** Warning message shown when confidence is low */
  lowConfidenceMessage?: string;
}

// ─── Full Decision Pack ─────────────────────────────────────────

export interface DecisionPackData {
  cover: CoverPageData;
  optionPages: OptionPageData[];
  summary: SummaryPageData;
  appendices: AppendixData[];
  /** Portrait SWOT Stack page (optional) */
  swotPage?: SwotPageData;
  /** Technical appendix with diagrams (optional) */
  technicalAppendix?: TechnicalAppendixData;
  /** Technical disclaimer page (optional) */
  technicalDisclaimer?: TechnicalDisclaimerData;
  /** Assumptions footer for PDF (small, boring, safe) */
  assumptionsFooter?: {
    text: string;  // e.g., "Assumptions current as of February 2026. Sources include Ofgem, DESNZ."
    engineVersion: string;
  };
  /** Property heat loss summary card (present when room-by-room data provided) */
  heatLossSummaryCard?: HeatLossSummaryCard;
}

// ─── Label helpers ──────────────────────────────────────────────

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  flat: 'flat/apartment',
  maisonette: 'maisonette',
  terraced: 'terraced',
  mid_terrace: 'mid-terrace',
  end_terrace: 'end-terrace',
  semi: 'semi-detached',
  semi_detached: 'semi-detached',
  detached: 'detached',
  bungalow: 'bungalow',
  semi_detached_bungalow: 'semi-detached bungalow',
};

const AGE_BAND_LABELS: Record<string, string> = {
  pre_1919: 'before 1919',
  '1919_1944': 'between 1919 and 1944',
  '1945_1964': 'between 1945 and 1964',
  '1965_1980': 'between 1965 and 1980',
  '1981_2000': 'between 1981 and 2000',
  '2001_2010': 'between 2001 and 2010',
  '2011_plus': 'after 2011',
};

const CURRENT_SYSTEM_LABELS: Record<string, string> = {
  combi: 'a combi boiler',
  system: 'a system boiler',
  regular: 'a regular (conventional) boiler',
  heat_pump: 'a heat pump',
  hybrid: 'a hybrid system',
};

// Experience question labels for "How you use your home"
const EXPERIENCE_LABELS: Record<string, Record<string, string>> = {
  simultaneousHotWater: {
    never: 'Rarely use multiple taps',
    occasionally: 'Sometimes use multiple taps',
    most_days: 'Often use multiple taps at once',
  },
  heatingDuringHotWater: {
    not_important: 'Heating during hot water not critical',
    important: 'Prefer heating to continue during hot water',
    very_important: 'Need heating during hot water use',
  },
  airingCupboard: {
    no: 'No airing cupboard use',
    occasionally: 'Occasionally use airing cupboard',
    yes_regularly: 'Regularly use airing cupboard',
  },
  spacePressure: {
    very_tight: 'Very limited space',
    some_flexibility: 'Some flexibility on space',
    plenty: 'Plenty of space available',
  },
  operatingStyle: {
    quick_response: 'Prefer quick heat response',
    balanced: 'Balanced heating style',
    steady_consistent: 'Prefer steady, consistent heat',
  },
  noiseSensitivity: {
    not_bothered: 'Not sensitive to noise',
    somewhat: 'Somewhat sensitive to noise',
    very_sensitive: 'Very sensitive to noise',
  },
  environmentalIntent: {
    nice_bonus: 'Green is a nice bonus',
    ready_for_later: 'Ready for future green options',
    greenest_now: 'Want greenest option now',
  },
};

/**
 * Build lifestyle summary from experience questions
 */
function buildLifestyleSummary(input: SystemRecInput): { items: Array<{ label: string; value: string }> } | undefined {
  const exp = input.experienceQuestions;
  if (!exp) return undefined;

  const items: Array<{ label: string; value: string }> = [];

  if (exp.simultaneousHotWater && EXPERIENCE_LABELS.simultaneousHotWater[exp.simultaneousHotWater]) {
    items.push({ label: 'Hot water use', value: EXPERIENCE_LABELS.simultaneousHotWater[exp.simultaneousHotWater] });
  }
  if (exp.operatingStyle && EXPERIENCE_LABELS.operatingStyle[exp.operatingStyle]) {
    items.push({ label: 'Heating style', value: EXPERIENCE_LABELS.operatingStyle[exp.operatingStyle] });
  }
  if (exp.spacePressure && EXPERIENCE_LABELS.spacePressure[exp.spacePressure]) {
    items.push({ label: 'Space', value: EXPERIENCE_LABELS.spacePressure[exp.spacePressure] });
  }
  if (exp.noiseSensitivity && EXPERIENCE_LABELS.noiseSensitivity[exp.noiseSensitivity]) {
    items.push({ label: 'Noise', value: EXPERIENCE_LABELS.noiseSensitivity[exp.noiseSensitivity] });
  }
  if (exp.environmentalIntent && EXPERIENCE_LABELS.environmentalIntent[exp.environmentalIntent]) {
    items.push({ label: 'Environment', value: EXPERIENCE_LABELS.environmentalIntent[exp.environmentalIntent] });
  }

  return items.length > 0 ? { items } : undefined;
}

const INSULATION_LEVEL_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

const EMITTER_TYPE_LABELS: Record<string, string> = {
  radiators: 'Radiators',
  underfloor: 'Underfloor',
  mixed: 'Mixed',
  unknown: 'Radiators (assumed)',
};

const LOW_TEMP_READINESS_LABELS: Record<string, string> = {
  low: 'Low (assumed)',
  medium: 'Medium',
  high: 'High',
};

function getPropertyTypeLabel(code?: string): string {
  if (!code) return 'home';
  return PROPERTY_TYPE_LABELS[code] || code;
}

function getAgeBandLabel(code?: string): string {
  if (!code || code === 'unknown') return '';
  return AGE_BAND_LABELS[code] || '';
}

function getCurrentSystemLabel(code?: string): string {
  if (!code) return 'an existing boiler';
  return CURRENT_SYSTEM_LABELS[code] || 'an existing boiler';
}

// ─── Comfort curve generation ───────────────────────────────────

function isHeatPumpOption(option: RecommendationOption): boolean {
  const st = option.systemType?.toLowerCase() || '';
  const b = option.boiler?.toLowerCase() || '';
  return st.includes('heat_pump') || b.includes('heat_pump') || b.includes('ashp');
}

/**
 * Deterministic comfort curve for heat pumps.
 * Slow, steady warm-up; very stable once warm.
 */
function heatPumpComfortCurve(): ComfortCurvePoint[] {
  return [
    { hour: 0, comfort: 30 },
    { hour: 1, comfort: 38 },
    { hour: 2, comfort: 48 },
    { hour: 3, comfort: 58 },
    { hour: 4, comfort: 66 },
    { hour: 5, comfort: 73 },
    { hour: 6, comfort: 79 },
    { hour: 7, comfort: 84 },
    { hour: 8, comfort: 88 },
    { hour: 10, comfort: 92 },
    { hour: 12, comfort: 94 },
    { hour: 16, comfort: 95 },
    { hour: 20, comfort: 96 },
    { hour: 24, comfort: 96 },
  ];
}

/**
 * Deterministic comfort curve for gas boilers.
 * Fast warm-up and recovery; stable with good controls.
 */
function gasBoilerComfortCurve(): ComfortCurvePoint[] {
  return [
    { hour: 0, comfort: 30 },
    { hour: 0.5, comfort: 50 },
    { hour: 1, comfort: 68 },
    { hour: 1.5, comfort: 78 },
    { hour: 2, comfort: 85 },
    { hour: 3, comfort: 90 },
    { hour: 4, comfort: 92 },
    { hour: 6, comfort: 93 },
    { hour: 8, comfort: 93 },
    { hour: 12, comfort: 93 },
    { hour: 16, comfort: 93 },
    { hour: 20, comfort: 93 },
    { hour: 24, comfort: 93 },
  ];
}

function getComfortBehaviour(option: RecommendationOption): ComfortBehaviour {
  if (isHeatPumpOption(option)) {
    return {
      archetype: 'STEADY_CONTINUOUS',
      chartTitle: 'Indoor Comfort Over Time',
      curve: heatPumpComfortCurve(),
      bullets: [
        'Slow, steady warm-up from cold',
        'Very stable comfort once warm',
        'Best suited to continuous or predictable heating',
      ],
    };
  }

  // Gas / default
  return {
    archetype: 'RESPONSIVE_BURST',
    chartTitle: 'Indoor Comfort Over Time',
    curve: gasBoilerComfortCurve(),
    bullets: [
      'Fast warm-up and recovery',
      'Stable comfort with good controls',
      'Flexible for changing routines',
    ],
  };
}

// ─── Works-best-when (occupancy-aware) ──────────────────────────

function getWorksBestWhen(
  option: RecommendationOption,
  _timing?: OccupancyTiming
): WorksBestWhen {
  if (isHeatPumpOption(option)) {
    return {
      bullet:
        'Works best when the home is kept at a steady temperature, especially with daytime occupancy.',
    };
  }

  return {
    bullet:
      'Works well for short heating periods, such as mornings and evenings.',
  };
}

// ─── Hot water suitability ──────────────────────────────────────

function getHotWaterSuitability(
  option: RecommendationOption,
  bathrooms: number
): HotWaterSuitability {
  const boiler = option.boiler?.toLowerCase() || '';
  const water = option.water?.toLowerCase() || '';

  const isCombi = boiler.includes('combi');
  const isUnvented = water.includes('unvented');
  const isSystem = boiler.includes('system');
  const isRegular = boiler.includes('regular');

  if (isCombi) {
    return {
      summary: '1 outlet at full flow, on-demand hot water',
      referenceText: 'See hot water comparison table in summary section',
    };
  }

  if (isSystem || isRegular) {
    const outletCount = isUnvented ? '2 outlets' : '1 outlet';
    return {
      summary: `${outletCount} simultaneously, stored hot water`,
      referenceText: 'See hot water comparison table in summary section',
    };
  }

  // Fallback for other systems
  return {
    summary: 'Hot water as configured',
    referenceText: 'See hot water comparison table in summary section',
  };
}

/**
 * Generate hot water comparison row for summary table
 */
function getHotWaterComparisonRow(option: RecommendationOption): HotWaterComparisonRow {
  const boiler = option.boiler?.toLowerCase() || '';
  const water = option.water?.toLowerCase() || '';
  const systemName = getSystemName(option);

  const isCombi = boiler.includes('combi');
  const isUnvented = water.includes('unvented');
  const isSystem = boiler.includes('system');
  const isRegular = boiler.includes('regular');
  const isHeatPump = isHeatPumpOption(option);

  if (isCombi) {
    return {
      option: systemName,
      simultaneousOutlets: '1 outlet at full flow',
      recoveryTime: 'Instant (on-demand)',
      pressureDependency: 'Dependent on mains pressure',
      storageVolume: 'No storage (on-demand)',
    };
  }

  if (isHeatPump) {
    return {
      option: systemName,
      simultaneousOutlets: '2+ outlets (with cylinder)',
      recoveryTime: '45-60 mins typical',
      pressureDependency: 'Mains pressure (unvented) or gravity (vented)',
      storageVolume: '150-300L typical',
    };
  }

  if (isSystem || isRegular) {
    if (isUnvented) {
      return {
        option: systemName,
        simultaneousOutlets: '2+ outlets simultaneously',
        recoveryTime: '20-30 mins typical',
        pressureDependency: 'Mains pressure (high flow)',
        storageVolume: '150-300L typical',
      };
    }
    return {
      option: systemName,
      simultaneousOutlets: '1-2 outlets (gravity limited)',
      recoveryTime: '30-45 mins typical',
      pressureDependency: 'Gravity-fed (lower pressure)',
      storageVolume: '100-200L typical',
    };
  }

  // Fallback
  return {
    option: systemName,
    simultaneousOutlets: 'Varies by configuration',
    recoveryTime: 'Varies',
    pressureDependency: 'Varies',
    storageVolume: 'Varies',
  };
}

// ─── Suitability for home ───────────────────────────────────────

function getSuitability(
  option: RecommendationOption,
  retrofitLevel: RetrofitLevel,
  lowTempReadiness: LowTempReadiness,
  emitterType: string,
  bathrooms: number
): SuitabilityForHome {
  const thingsToBeAware: string[] = [];

  // Only show if applicable
  if (lowTempReadiness === 'low' && isHeatPumpOption(option)) {
    thingsToBeAware.push(
      'Some radiators may need upgrading for best comfort and efficiency.'
    );
    thingsToBeAware.push(
      'Low-temp readiness is inferred from available data — an in-home survey will confirm whether radiator upgrades are needed.'
    );
  }
  // Show insulation advice for low and medium retrofit levels
  // (only skip for homes with already-high insulation)
  if (retrofitLevel === 'low' || retrofitLevel === 'medium') {
    thingsToBeAware.push('Improving insulation benefits any heating system.');
  }

  return {
    hotWater: getHotWaterSuitability(option, bathrooms),
    whatHelps: {
      insulationLevel: INSULATION_LEVEL_LABELS[retrofitLevel] || 'Medium',
      emitters: EMITTER_TYPE_LABELS[emitterType] || 'Radiators (assumed)',
      lowTempReadiness: LOW_TEMP_READINESS_LABELS[lowTempReadiness] || 'Medium',
    },
    thingsToBeAware,
  };
}

// ─── Annual cost extraction ─────────────────────────────────────

function getAnnualCost(
  option: RecommendationOption,
  output: SystemRecOutput
): AnnualRunningCost {
  // Current system cost from year1 baseline
  const currentCost = output.year1?.baseline?.costGbp ?? null;

  // Option cost: prefer year1 match, fallback to deltaVsBaseline
  let optionCost: number | null = null;
  if (output.year1?.options) {
    const match = output.year1.options.find((o) => o.optionId === option.id);
    if (match) {
      optionCost = match.impact.costGbp;
    }
  }
  if (optionCost === null && option.deltaVsBaseline) {
    optionCost = option.deltaVsBaseline.optionAnnualized_gbp;
  }

  return {
    currentSystemCostPerYear: currentCost,
    thisOptionCostPerYear: optionCost,
    footnote:
      'Estimates reflect your home size, insulation, emitters, and how the home is typically occupied.',
  };
}

// ─── Human-readable system name ─────────────────────────────────

/**
 * Get system diagram key for print/PDF rendering.
 * Returns stable enum that the rendering layer maps to SVG assets.
 */
function getSystemDiagramKey(option: RecommendationOption): SystemDiagramKey {
  const systemType = option.systemType;
  const isCombi = option.boiler?.toLowerCase().includes('combi');
  const isSystem = option.boiler?.toLowerCase().includes('system');
  const isRegular = option.boiler?.toLowerCase().includes('regular');
  
  if (systemType === 'heat_pump') {
    return 'heat_pump';
  }
  if (isCombi) {
    return 'combi';
  }
  if (isSystem) {
    return 'system_cylinder';
  }
  if (isRegular) {
    // Distinguish between vented and unvented regular systems
    const water = option.water?.toLowerCase() || '';
    const isUnvented = water.includes('unvented');
    return isUnvented ? 'regular_unvented' : 'regular_vented';
  }
  
  // Default fallback
  return 'unknown';
}

function getSystemName(option: RecommendationOption): string {
  // Use boilerLabel + waterLabel for human-readable name
  const parts: string[] = [];
  if (option.boilerLabel) parts.push(option.boilerLabel);
  if (option.waterLabel) parts.push(`with ${option.waterLabel.toLowerCase()}`);
  if (parts.length > 0) return parts.join(' ');

  // Fallback to title
  return option.title;
}

function getComfortStyle(option: RecommendationOption): string {
  if (isHeatPumpOption(option)) return 'Steady, continuous';
  return 'Responsive, flexible';
}

function getBestFor(option: RecommendationOption): string {
  if (isHeatPumpOption(option)) return 'Predictable routines, daytime occupancy';
  return 'Varied routines, burst heating';
}

// ─── SWOT Page Builders ─────────────────────────────────────────

/**
 * Build SWOT nodes from system characteristics
 * This creates a "before and after" view showing Status Quo vs Option A
 */
function buildSwotPage(
  output: SystemRecOutput,
  input: SystemRecInput,
  options: RecommendationOption[]
): SwotPageData | undefined {
  // Only generate if we have both baseline and a recommended option
  if (!output.primary || options.length === 0) return undefined;

  const primaryOption = options[0]; // Option A
  
  // Status Quo SWOT - extract from input data
  const statusQuoQuadrants = buildStatusQuoSwot(input, output);
  
  // Option A SWOT - extract from recommended option
  const optionAQuadrants = buildOptionASwot(primaryOption, input, output);

  return {
    title: 'System Comparison',
    statusQuo: {
      label: 'Current System (Status Quo)',
      quadrants: statusQuoQuadrants,
    },
    optionA: {
      label: primaryOption.title,
      quadrants: optionAQuadrants,
    },
  };
}

/**
 * Build Status Quo SWOT quadrants from input data
 */
function buildStatusQuoSwot(input: SystemRecInput, output: SystemRecOutput): SwotQuadrantData {
  const strengths: SwotNodeData[] = [];
  const weaknesses: SwotNodeData[] = [];
  const opportunities: SwotNodeData[] = [];
  const threats: SwotNodeData[] = [];

  // Weaknesses from current system
  if (input.systemAge === '15-20' || input.systemAge === '20+') {
    weaknesses.push({
      id: 'old-boiler',
      label: 'Aging Boiler',
      icon: '🔥',
      impactWeight: input.systemAge === '20+' ? 1.5 : 1.3,
    });
  }

  if (input.sludgeRisk === 'confirmed' || input.sludgeRisk === 'likely') {
    weaknesses.push({
      id: 'sludge',
      label: 'Magnetite Sludge',
      icon: '💧',
      impactWeight: input.sludgeRisk === 'confirmed' ? 1.3 : 1.0,
    });
  }

  if (input.insulationLevel === 'poor') {
    weaknesses.push({
      id: 'poor-insulation',
      label: 'Poor Insulation',
      icon: '🏠',
      impactWeight: 1.2,
    });
  }

  if (input.pipeBore && ['8mm', '10mm'].includes(input.pipeBore)) {
    weaknesses.push({
      id: 'small-pipes',
      label: 'Undersized Pipework',
      icon: '🔧',
      impactWeight: 1.1,
    });
  }

  if (input.controlType === 'basic' || input.controlType === 'none') {
    weaknesses.push({
      id: 'basic-controls',
      label: 'Inefficient Controls',
      icon: '🎛️',
      impactWeight: 0.9,
    });
  }

  // Threats
  if (input.systemAge === '15-20' || input.systemAge === '20+') {
    threats.push({
      id: 'reliability-risk',
      label: 'Critical Reliability Risk',
      icon: '⚠️',
      impactWeight: input.systemAge === '20+' ? 1.5 : 1.3,
    });
  }

  if (input.waterHardness === 'hard' && !input.hasFilter) {
    threats.push({
      id: 'scaling',
      label: 'Scaling Damage',
      icon: '🧪',
      impactWeight: 1.1,
    });
  }

  // Strengths - minimal for failing system
  // Threshold: £1500/year represents typical lower cost for aging but functional system
  if (output.baseline?.annualCostGbp && output.baseline.annualCostGbp < 1500) {
    strengths.push({
      id: 'current-cost',
      label: 'Reliable Until Now',
      icon: '✓',
      impactWeight: 0.5,
    });
  }

  // Opportunities - none for status quo (it's the problem)

  return { strengths, weaknesses, opportunities, threats };
}

/**
 * Build Option A SWOT quadrants from recommended option
 */
function buildOptionASwot(
  option: RecommendationOption,
  input: SystemRecInput,
  output: SystemRecOutput
): SwotQuadrantData {
  const strengths: SwotNodeData[] = [];
  const weaknesses: SwotNodeData[] = [];
  const opportunities: SwotNodeData[] = [];
  const threats: SwotNodeData[] = [];

  const isHeatPump = isHeatPumpOption(option);
  const hasModulation = option.boilerLabel?.toLowerCase().includes('modulating') || 
                        option.boilerLabel?.toLowerCase().includes('condensing');

  // Strengths of new option
  if (hasModulation) {
    strengths.push({
      id: 'modulation',
      label: 'Condensing Modulation',
      icon: '♨️',
      impactWeight: 1.4,
    });
  }

  if (option.waterLabel?.toLowerCase().includes('s-plan') || 
      option.topology === 's_plan') {
    strengths.push({
      id: 's-plan',
      label: 'S-Plan Topology',
      icon: '🔀',
      impactWeight: 1.2,
    });
  }

  if (input.hasFilter || option.includesFilter) {
    strengths.push({
      id: 'laminar-filter',
      label: 'Laminar Flow Filter',
      icon: '💧',
      impactWeight: 1.3,
    });
  }

  if (option.controlType === 'smart' || option.controlType === 'load_compensation') {
    strengths.push({
      id: 'smart-controls',
      label: 'Advanced Controls',
      icon: '🎛️',
      impactWeight: 1.2,
    });
  }

  strengths.push({
    id: 'new-system',
    label: 'New Equipment',
    icon: '✨',
    impactWeight: 1.0,
  });

  // Opportunities
  if (isHeatPump) {
    opportunities.push({
      id: 'bus-grant',
      label: '£7,500 BUS Grant',
      icon: '💷',
      impactWeight: 1.5,
    });
  }

  if (option.controlType === 'smart') {
    opportunities.push({
      id: 'smart-tariff',
      label: 'Smart Tariff Ready',
      icon: '⚡',
      impactWeight: 1.0,
    });
  }

  // Weaknesses - be honest
  if (option.estimatedInstallCostGbp && option.estimatedInstallCostGbp > 3000) {
    weaknesses.push({
      id: 'upfront-cost',
      label: 'Upfront Investment',
      icon: '💰',
      impactWeight: 0.8,
    });
  }

  if (option.waterLabel?.toLowerCase().includes('cylinder') || 
      option.waterLabel?.toLowerCase().includes('unvented')) {
    weaknesses.push({
      id: 'airing-cupboard',
      label: 'Airing Cupboard Space',
      icon: '📦',
      impactWeight: 0.6,
    });
  }

  // Threats - minimal for new option
  // (none - it's the solution)

  return { strengths, weaknesses, opportunities, threats };
}

/**
 * Build Technical Appendix with conditional diagram injection
 */
function buildTechnicalAppendix(
  output: SystemRecOutput,
  options: RecommendationOption[]
): TechnicalAppendixData | undefined {
  const diagrams: TechnicalDiagramData[] = [];

  // Check if Laminar Filter is in Option A - inject Cyclonic Physics diagram
  const primaryOption = options[0];
  if (primaryOption && (primaryOption.includesFilter || primaryOption.title.toLowerCase().includes('filter'))) {
    diagrams.push({
      id: 'laminar-flow',
      title: 'Laminar Flow vs. Turbulence',
      diagramType: 'laminar-flow',
      description: 'High-fidelity diagram showing how cyclonic filtration works to trap particles while maintaining smooth flow.',
    });
  }

  // Check for S-Plan topology - inject topology diagram
  if (primaryOption && (primaryOption.topology === 's_plan' || primaryOption.waterLabel?.toLowerCase().includes('s-plan'))) {
    diagrams.push({
      id: 's-plan-topology',
      title: 'System Topology: S-Plan vs. Y-Plan',
      diagramType: 's-plan-topology',
      description: 'Diagram showing "Shared Junctions" (Y-Plan) vs. "Express Lanes" (S-Plan) and their impact on system efficiency.',
    });
  }

  // Always include modulation diagram for modern systems
  if (primaryOption && (primaryOption.boilerLabel?.toLowerCase().includes('condensing'))) {
    diagrams.push({
      id: 'modulation-cycling',
      title: 'Modulation vs. Cycling',
      diagramType: 'modulation-cycling',
      description: 'Shows the difference between smooth modulation and on/off cycling and their impact on efficiency and longevity.',
    });
  }

  return diagrams.length > 0 ? {
    title: 'Technical Details',
    diagrams,
  } : undefined;
}

/**
 * Build Technical Disclaimer page with engine assumptions and calculations
 */
function buildTechnicalDisclaimer(
  output: SystemRecOutput,
  input: SystemRecInput
): TechnicalDisclaimerData {
  const engineAssumptions: EngineAssumptionRow[] = [];

  // Wall U-value based on property age
  if (input.propertyAgeBand) {
    const ageBandLabels: Record<string, string> = {
      'pre_1919': 'Pre-1919 (solid wall, 2.0 W/m²K)',
      '1919_1944': 'Inter-war period (1.7 W/m²K)',
      '1945_1964': 'Post-war period (1.5 W/m²K)',
      '1965_1980': '1960s-70s build (1.0 W/m²K)',
      '1981_2000': '1980s-90s build (0.6 W/m²K)',
      '2001_2010': '2000s build (0.35 W/m²K)',
      '2011_plus': 'Modern build (0.30 W/m²K)',
    };
    engineAssumptions.push({
      parameter: 'Wall U-value',
      assumed: ageBandLabels[input.propertyAgeBand] || 'Typical for age',
      source: 'Property age band',
    });
  }

  // Floor area assumption
  if (input.bedrooms) {
    const AREA_PER_BEDROOM = 15; // m² per bedroom
    const BASE_COMMON_AREA = 30; // m² for kitchen, living room, bathrooms, hallways
    const bedroomCount = parseInt(String(input.bedrooms), 10);
    const estimatedArea = bedroomCount * AREA_PER_BEDROOM + BASE_COMMON_AREA;
    engineAssumptions.push({
      parameter: 'Floor area',
      assumed: `~${estimatedArea}m² (${input.bedrooms} bed)`,
      source: 'Bedroom count',
    });
  }

  // Design temperature
  engineAssumptions.push({
    parameter: 'Design outside temp',
    assumed: '-3°C (UK design)',
    source: 'CIBSE Guide A',
  });

  engineAssumptions.push({
    parameter: 'Design inside temp',
    assumed: '21°C living, 18°C sleeping',
    source: 'CIBSE Guide A',
  });

  // Calculated loads
  const calculatedLoads: CalculatedLoadRow[] = [];

  if (output.derivedMetrics?.peakHeatLossW) {
    calculatedLoads.push({
      metric: 'Peak heat loss',
      value: (output.derivedMetrics.peakHeatLossW / 1000).toFixed(1),
      unit: 'kW',
    });
  }

  if (output.derivedMetrics?.flowTempEstimate) {
    calculatedLoads.push({
      metric: 'Target flow temp',
      value: output.derivedMetrics.flowTempEstimate.toString(),
      unit: '°C',
    });
  }

  if (output.primary?.estimatedInstallCostGbp) {
    calculatedLoads.push({
      metric: 'Est. install cost',
      value: output.primary.estimatedInstallCostGbp.toLocaleString(),
      unit: 'GBP',
    });
  }

  // Disclaimers
  const disclaimers = [
    'Values are indicative based on non-invasive survey and typical assumptions for similar properties.',
    'Fuel price fluctuations may significantly affect running costs. Current prices used as baseline.',
    'Subject to full design survey and structural assessment before installation.',
    'Grant availability and eligibility subject to change. Check current schemes before proceeding.',
    'System performance depends on proper installation, commissioning, and ongoing maintenance.',
  ];

  return {
    title: 'Technical Assumptions & Disclaimers',
    engineAssumptions,
    calculatedLoads,
    disclaimers,
  };
}

// ─── Builder ────────────────────────────────────────────────────

/**
 * Build the complete Decision Pack data contract from engine output.
 *
 * This is the single translation point between engine truth and printed output.
 * The print layer consumes DecisionPackData and renders it verbatim.
 */
export function buildDecisionPack(
  output: SystemRecOutput,
  input: SystemRecInput,
  selectedOptionIds?: string[]
): DecisionPackData {
  // Derived metrics (from engine)
  const retrofitLevel = output.derivedMetrics?.retrofitLevel ?? 'medium';
  const lowTempReadiness = output.derivedMetrics?.lowTempReadiness ?? 'medium';
  const emitterType = output.derivedMetrics?.emitterProfile?.type ?? 'unknown';

  // Collect all options, filter to selected if provided
  const allOptions = [output.primary, ...(output.alternatives || [])].filter(Boolean);
  const optionsToRender = selectedOptionIds
    ? allOptions.filter((o) => selectedOptionIds.includes(o.id))
    : allOptions;

  // ── Cover ──
  const propertyTypeLabel = getPropertyTypeLabel(input.propertyType);
  const ageBandLabel = getAgeBandLabel(input.propertyAgeBand);
  const currentSystemLabel = getCurrentSystemLabel(input.currentBoiler || input.existingSystem);
  const bedrooms = input.bedrooms ?? '—';
  const bathrooms = input.bathrooms ?? 1;

  const builtClause = ageBandLabel ? ` built ${ageBandLabel}` : '';
  const openingParagraph =
    `This recommendation is based on your ${propertyTypeLabel} home${builtClause}, ` +
    `currently heated by ${currentSystemLabel}, with ${bedrooms} bedrooms and ` +
    `${bathrooms} bathroom${Number(bathrooms) !== 1 ? 's' : ''}. ` +
    `The heating options presented balance comfort, running costs, and ` +
    `reliability without assuming major changes to your home.`;

  const occupancyLine = getOccupancyTimingLabel(input.occupancyTiming);

  const hasAssumptions =
    (output.homeSummaryFacts?.assumptionsUsed?.length ?? 0) > 0;
  const assumptionsLine = hasAssumptions
    ? 'Where information was unknown (such as insulation or radiator sizing), typical assumptions for homes like yours were used.'
    : null;

  const cover: CoverPageData = {
    title: 'Heating system recommendations for your home',
    openingParagraph,
    occupancyLine: `How your home is used: ${occupancyLine}`,
    lifestyleSummary: buildLifestyleSummary(input),
    assumptionsLine,
    generatedDate: new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }),
  };

  // ── Option pages ──
  const planningNote =
    'Planning ahead gives you more choice and avoids rushed decisions if the boiler fails unexpectedly.';

  // Determine microbore note based on pipe bore
  const pipeBore = output.derivedMetrics?.emitterProfile?.pipeBore;
  const pipeBoreNote = (pipeBore === '8mm' || pipeBore === '10mm')
    ? 'Your radiator pipework uses microbore pipes. Low-temperature heating upgrades (such as heat pumps) may require a pipework review to ensure adequate flow.'
    : undefined;

  const optionPages: OptionPageData[] = optionsToRender.map((option) => {
    // Determine if this is a gas boiler option (show control precision)
    const isGasBoiler = !isHeatPumpOption(option);

    return {
      optionId: option.id,
      header: {
        systemName: getSystemName(option),
        subLine: 'How this system behaves in everyday use',
      },
      systemDiagramKey: getSystemDiagramKey(option),
      comfort: getComfortBehaviour(option),
      worksBestWhen: getWorksBestWhen(option, input.occupancyTiming),
      suitability: getSuitability(option, retrofitLevel, lowTempReadiness, emitterType, bathrooms),
      annualCost: getAnnualCost(option, output),
      planningNote,
      // Include all experience fit factors (not just those with comments/conflicts)
      // This ensures the RAG dots are always visible in the Decision Pack
      experienceFit: option.experienceFit?.map(f => ({
        factor: f.factor,
        dots: f.dots,
        verdict: f.verdict,
        comment: f.comment,
        isConflict: f.isConflict,
        conflictNote: f.conflictNote,
      })),
      // Control precision for gas boiler options
      controlPrecision: isGasBoiler ? {
        showSection: true,
        boilerSupportsLoadCompensation: true, // Modern boilers typically support this
      } : undefined,
      // Microbore note for heat pump options
      pipeBoreNote: isHeatPumpOption(option) ? pipeBoreNote : undefined,
    };
  });

  // ── Summary ──
  const summaryRows: SummaryRow[] = optionsToRender.map((option) => {
    const cost = getAnnualCost(option, output);
    const costStr =
      cost.thisOptionCostPerYear !== null
        ? `£${Math.round(cost.thisOptionCostPerYear).toLocaleString()} per year`
        : '—';
    return {
      option: getSystemName(option),
      comfortStyle: getComfortStyle(option),
      bestFor: getBestFor(option),
      typicalAnnualCost: costStr,
    };
  });

  // ── Hot Water Comparison Table (fixes the "See summary table" reference) ──
  const hotWaterComparison: HotWaterComparisonRow[] = optionsToRender.map((option) =>
    getHotWaterComparisonRow(option)
  );

  const summary: SummaryPageData = {
    rows: summaryRows,
    hotWaterComparison,
    nextSteps: [
      'Choose the option that best matches how you heat your home',
      'Confirm any installation considerations with your advisor',
      'Decide whether to act now or monitor the current system',
    ],
    // Add confidence summary (condensed for print)
    confidenceSummary: output.confidenceSummary ? {
      level: output.confidenceSummary.level,
      reasons: output.confidenceSummary.reasons.slice(0, 2),  // max 2 for print
    } : undefined,
    // Add red flags (condensed)
    redFlags: output.redFlags?.slice(0, 3).map(flag => ({
      title: flag.title,
      detail: flag.detail,
      severity: flag.severity,
    })),
    // Add what-could-change bullets (max 3 for print)
    whatCouldChange: output.whatCouldChangeRecommendation?.slice(0, 3),
  };

  // ── Appendices (merged by concept: 4 → 2, plus 2 new for explainability) ──
  const appendices: AppendixData[] = [
    {
      id: 'how-systems-stay-efficient',
      title: 'How Systems Stay Efficient',
      content: [
        // Clean Systems concept
        'Clean water allows full modulation. A clean water circuit allows a boiler to run efficiently, with smooth continuous modulation rather than frequent on/off cycles.',
        'Full modulation reduces cycling. Clean systems demonstrate smooth, continuous modulation with less mechanical stress, extending component life and improving reliability.',
        // Controls & Modulation concept
        'Load compensation measures the gap between room temperature and setpoint, then tells the boiler exactly how much heat to produce.',
        'The result is steadier comfort, less fuel used, and less wear on components. A boiler running gently for longer is more efficient than one firing at full power in short bursts.',
      ],
    },
    {
      id: 'power-flush-filters',
      title: 'Power Flushing & Laminar Flow Filters',
      content: [
        'Power flushing clears historic sludge and debris from radiators, pipework, and heat exchangers so a system can return to clean, efficient heat transfer.',
        'Laminar flow filters are inline filters that slow and straighten the water path, trapping fine particles while maintaining smooth circulation and protecting pumps and valves.',
        'Used together with inhibitor chemicals, they help keep systems clean long-term: deep clean once, then protect continuously.',
      ],
    },
    {
      id: 'why-design-affects-longevity',
      title: 'Why Design Affects Longevity',
      content: [
        // Pipework & Flow concept
        'Larger pipes carry more water at lower velocity, meaning less turbulence, less noise, and gentler heat delivery.',
        'Oversized radiators or underfloor circuits work at lower flow temperatures, which is exactly what condensing boilers and heat pumps need to operate efficiently.',
        // Same Comfort, Different Stress concept
        'Two systems can maintain the same temperature and deliver the same hot water over time. The difference is how that work is delivered — steady versus spiky output.',
        'Start/stop events create thermal and mechanical stress. Steady operation reduces stress and improves efficiency. Different designs prioritise different outcomes: compactness, responsiveness, steadiness, longevity.',
      ],
    },
    {
      id: 'system-layout-pipework',
      title: 'Why System Layout and Pipe Sizing Matter',
      content: [
        // Content is rendered by the AppendixExplainer component with the motorway analogy
        // This entry exists to register the appendix in the pack
      ],
    },
    {
      id: 'where-energy-is-lost',
      title: 'Where energy is lost before it reaches your home',
      content: [
        'Two systems can deliver the same warmth to your home. The difference is how much energy is lost along the way.',
        'This does not mean modern systems create extra heat — they reduce losses between fuel and comfort.',
        'Even modern systems still lose some energy — no system is perfect.',
      ],
    },
    {
      id: 'heat-pump-electricity-source',
      title: 'How electricity source affects heat pump performance',
      content: [
        "A heat pump's performance depends not just on the system, but on where the electricity comes from.",
        'When electricity is generated from cleaner sources, more of the original energy ends up as useful heat in your home. As the grid becomes cleaner, heat pumps automatically become more effective over time.',
        'Some electricity tariffs are designed to suit systems that run steadily over longer periods. These can significantly change running costs.',
        'This means the same heat pump can improve in performance over its lifetime as the electricity system improves.',
      ],
    },
  ];

  // Build assumptions footer if manifest is available
  const assumptionsFooter = output.assumptionManifest
    ? {
        text: formatManifestForPDF(output.assumptionManifest),
        engineVersion: output.assumptionManifest.engine_version || ENGINE_VERSION,
      }
    : undefined;

  // ── Build heat loss summary card ──
  // Prefer room-by-room result; fall back to simple whole-house result.
  let heatLossSummaryCard: import('./presentationPrint').HeatLossSummaryCard | undefined;
  if (output.heatLossSummary) {
    const s = output.heatLossSummary;
    heatLossSummaryCard = {
      designTemps: `${s.designTempInternal}°C / ${s.designTempExternal}°C`,
      totalHeatLossKW: s.totalHeatLossKW,
      transmissionLossKW: s.transmissionLossKW,
      ventilationLossKW: s.ventilationLossKW,
      confidence: s.confidence,
      interpretation: s.interpretation,
      lowConfidenceFlag: s.lowConfidenceFlag,
      lowConfidenceMessage: s.lowConfidenceFlag
        ? 'Sizing confidence moderate — verify before final specification.'
        : undefined,
    };
  } else if (output.simpleHeatLossResult) {
    const r = output.simpleHeatLossResult;
    const band = getHeatLossSuitabilityBand(r.totalHeatLossKW);
    const interpretations: Record<string, string> = {
      low_temp_suitable: 'Low heat demand — well suited to low-temperature systems.',
      standard_boiler: 'Typical demand — standard sizing range.',
      higher_capacity: 'Higher demand — emitter review and sizing verification recommended.',
    };
    heatLossSummaryCard = {
      designTemps: `${r.designTempInternalC}°C / ${r.designTempExternalC}°C`,
      totalHeatLossKW: Math.round(r.totalHeatLossKW * 10) / 10,
      transmissionLossKW: Math.round((r.transmissionLossW / 1000) * 10) / 10,
      ventilationLossKW: Math.round((r.ventilationLossW / 1000) * 10) / 10,
      confidence: r.confidence0to100,
      sizingTargetKW: Math.round(r.sizingTargetKW * 10) / 10,
      notes: r.notes,
      interpretation: interpretations[band] ?? '',
      lowConfidenceFlag: r.confidence0to100 < 60,
      lowConfidenceMessage: r.confidence0to100 < 60
        ? 'Sizing confidence low — verify before final specification.'
        : undefined,
    };
  }

  // ── Build SWOT Page (Portrait No-Brainer) ──
  const swotPage = buildSwotPage(output, input, optionsToRender);

  // ── Build Technical Appendix ──
  const technicalAppendix = buildTechnicalAppendix(output, optionsToRender);

  // ── Build Technical Disclaimer ──
  const technicalDisclaimer = buildTechnicalDisclaimer(output, input);

  return {
    cover,
    optionPages,
    summary,
    appendices,
    swotPage,
    technicalAppendix,
    technicalDisclaimer,
    assumptionsFooter,
    heatLossSummaryCard,
  };
}

/**
 * Validate a rendered Decision Pack against the hard acceptance rules.
 * Returns an array of failures (empty = pass).
 */
export function validateDecisionPack(renderedText: string): string[] {
  const failures: string[] = [];

  // Check banned terms
  const lowerText = renderedText.toLowerCase();
  for (const term of BANNED_TERMS) {
    if (lowerText.includes(term.toLowerCase())) {
      failures.push(`Banned term found: "${term}"`);
    }
  }

  // Must include occupancy wording
  if (!lowerText.includes('how your home is used')) {
    failures.push('Missing occupancy wording');
  }

  // Must include £/year costs
  if (!renderedText.includes('£') || !lowerText.includes('per year')) {
    failures.push('Missing £/year cost display');
  }

  return failures;
}

/**
 * Format a cost number for display: £X,XXX per year
 */
export function formatCostPerYear(amount: number | null): string {
  if (amount === null || !isFinite(amount)) return '—';
  return `£${Math.round(amount).toLocaleString()} per year`;
}
